/**
 * Left column for the Binom paths workspace — rule groups on top, default
 * paths below. Drag-to-reorder via @dnd-kit/sortable; "+ Path" button creates
 * a fresh draft entry the C1 merger writes as a new Binom path.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { PATH_LIST_CONTAINER_CLASS } from '@/features/flow-optimizer/tabs/standard/v2/paths/path-list-chrome';
import {
  usePreviewMode,
  usePreviewRowLookup,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import {
  PreviewSortLabel,
  usePreviewMetricSort,
} from '@/features/flow-optimizer/components/preview/sort';

import type {
  BinomDraftPath,
  BinomFlowDraft,
} from '../binom-flow-draft.types';

import BinomDraggablePathRow from './list/BinomDraggablePathRow';
import BinomRuleGroupCard from './list/BinomRuleGroupCard';
import {
  defaultPathSelection,
  isDefaultPathSelected,
  type BinomPathsSelection,
} from './binom-paths-selection';
import { pathSharePercents } from './list/path-share-percent';
import {
  dragEndToMove,
  useBinomDragSensors,
} from './list/use-binom-drag-sensors';

export interface BinomPathsLeftColumnProps {
  draft: BinomFlowDraft;
  selection: BinomPathsSelection;
  onSelect: (next: BinomPathsSelection) => void;
  patchDefaultPath: (idx: number, m: (p: BinomDraftPath) => void) => void;
  patchGroupPath: (
    groupIdx: number,
    pathIdx: number,
    m: (p: BinomDraftPath) => void,
  ) => void;
  onAddDefaultPath: () => void;
  onMoveDefaultPath: (from: number, to: number) => void;
  onAddGroupPath: (groupIdx: number) => void;
  onMoveGroupPath: (groupIdx: number, from: number, to: number) => void;
}

const BinomPathsLeftColumn: FC<BinomPathsLeftColumnProps> = ({
  draft,
  selection,
  onSelect,
  patchDefaultPath,
  patchGroupPath,
  onAddDefaultPath,
  onMoveDefaultPath,
  onAddGroupPath,
  onMoveGroupPath,
}) => {
  const previewMode = usePreviewMode();
  const lookup = usePreviewRowLookup();
  const { sort, cycle, sortEntries } = usePreviewMetricSort();
  const percents = pathSharePercents(draft.defaultPaths);
  const sensors = useBinomDragSensors();
  const ids = draft.defaultPaths.map((p) => p.id);
  const defaultEntries = useMemo(
    () =>
      sortEntries(
        draft.defaultPaths.map((path, index) => ({ path, index })),
        ({ path }) => ({
          active: path.active !== false,
          name: path.name ?? '',
          row: lookup('paths', path.id),
        }),
      ),
    [draft.defaultPaths, sortEntries, lookup],
  );

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col gap-2 overflow-y-auto pr-0 lg:basis-[30%] lg:flex-none lg:pr-2">
      {draft.pathGroups.length > 0 && (
        <div className="space-y-2">
          {draft.pathGroups.map((group, groupIdx) => (
            <BinomRuleGroupCard
              key={group.id}
              group={group}
              groupIndex={groupIdx}
              selection={selection}
              onSelect={onSelect}
              onPatchPath={(pathIdx, m) => patchGroupPath(groupIdx, pathIdx, m)}
              onAddPath={() => onAddGroupPath(groupIdx)}
              onMovePath={(from, to) => onMoveGroupPath(groupIdx, from, to)}
            />
          ))}
        </div>
      )}

      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-[10px] font-semibold uppercase tracking-wide text-foreground">
            Default paths
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {draft.defaultPaths.length}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-[10px]"
              onClick={onAddDefaultPath}
              aria-label="Add default path"
            >
              <Plus className="h-3 w-3" aria-hidden /> Path
            </Button>
          </div>
        </div>
        {draft.defaultPaths.length === 0 ? (
          <p className="text-[11px] italic text-muted-foreground">
            No default paths yet — click "+ Path" to add one.
          </p>
        ) : previewMode ? (
          <>
            <div className="flex items-stretch">
              <span className="w-4 shrink-0" aria-hidden />
              <div className="min-w-0 flex-1 px-1.5">
                <div className="grid w-full items-center gap-x-1.5 grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]">
                  <span />
                  <PreviewSortLabel column="current" label="Current" sort={sort} onCycle={cycle} />
                  <span />
                  <PreviewSortLabel column="new" label="New" sort={sort} onCycle={cycle} />
                  <PreviewSortLabel column="diff" label="Diff" sort={sort} onCycle={cycle} />
                  <span />
                </div>
              </div>
            </div>
          <div className={PATH_LIST_CONTAINER_CLASS} role="listbox" aria-label="Default paths">
            {defaultEntries.map(({ path, index: idx }) => (
              <BinomDraggablePathRow
                key={path.id}
                id={path.id}
                path={path}
                weightPercent={percents[idx] ?? 0}
                selected={isDefaultPathSelected(selection, idx)}
                onSelect={() => onSelect(defaultPathSelection(idx))}
                onPatch={(m) => patchDefaultPath(idx, m)}
                dragDisabled
              />
            ))}
          </div>
          </>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => {
              const m = dragEndToMove(e, ids);
              if (m) onMoveDefaultPath(m.from, m.to);
            }}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div
                className={PATH_LIST_CONTAINER_CLASS}
                role="listbox"
                aria-label="Default paths"
              >
                {draft.defaultPaths.map((p, idx) => (
                  <BinomDraggablePathRow
                    key={p.id}
                    id={p.id}
                    path={p}
                    weightPercent={percents[idx] ?? 0}
                    selected={isDefaultPathSelected(selection, idx)}
                    onSelect={() => onSelect(defaultPathSelection(idx))}
                    onPatch={(m) => patchDefaultPath(idx, m)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

export default BinomPathsLeftColumn;
